import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * A floating Scroll-To-Top button that stays fixed in the bottom-right corner.
 * Always visible as requested. Uses smooth scroll.
 */
const ScrollToTopButton: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const rafId = useRef<number | null>(null);

    useEffect(() => {
        const threshold = 120; // px from top before showing
        const onScroll = () => {
            if (rafId.current != null) return;
            rafId.current = requestAnimationFrame(() => {
                rafId.current = null;
                setVisible(window.scrollY > threshold);
            });
        };

        // Initialize on mount
        setVisible(window.scrollY > threshold);

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId.current) cancelAnimationFrame(rafId.current);
        };
    }, []);

    const handleClick = useCallback(() => {
        try {
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            // Fallback for older browsers
            window.scrollTo(0, 0);
        }
    }, []);

    return (
        <div
            className={"scroll-to-top-container" + (visible ? "" : " scroll-to-top--hidden")}
            aria-hidden={!visible}
        >
            <button
                type="button"
                onClick={handleClick}
                aria-label="Scroll to top"
                title="Scroll to top"
                className="scroll-to-top-button"
            >
                <i className="bi bi-chevron-up scroll-to-top-icon" aria-hidden="true" />
                <span aria-hidden="true" className="scroll-to-top-icon" style={{ display: "none" }}>
        ▲
      </span>
            </button>
        </div>
    );
};

export default ScrollToTopButton;